if (!customElements.get('preme-reels')) {
  customElements.define('preme-reels', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.track = this.querySelector('.preme-reels__track');
      if (!this.track) return;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.cards = [...this.querySelectorAll('.preme-reels__card')];
      this.videos = [...this.querySelectorAll('video')];
      this.previous = this.querySelector('[data-previous]');
      this.next = this.querySelector('[data-next]');
      this.position = this.querySelector('.preme-reels__position');
      this.controls = this.querySelector('.preme-reels__controls');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        const button = card.querySelector('[data-play]');
        if (!video || !button) return;
        button.hidden = false;
        button.removeAttribute('aria-busy');
        if (!video.querySelector('source[src]')) video.hidden = true;
        button.addEventListener('click', () => this.playCard(card), { signal });
        video.addEventListener('playing', () => {
          button.hidden = true;
          button.removeAttribute('aria-busy');
          card.querySelector('.preme-reels__status').textContent = '';
          if (document.activeElement === button) video.focus({ preventScroll: true });
        }, { signal });
        video.addEventListener('error', () => this.showError(card), { signal });
      });
      this.previous?.addEventListener('click', () => this.move(-1), { signal });
      this.next?.addEventListener('click', () => this.move(1), { signal });
      this.track.addEventListener('scroll', () => this.updateControls(), { signal, passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.videos.forEach((video) => video.pause());
      }, { signal });
      if (window.IntersectionObserver) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) entry.target.querySelector('video')?.pause();
          });
        }, { threshold: 0.1 });
        this.cards.forEach((card) => this.observer.observe(card));
      }
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', () => this.updateControls(), { signal });
      }
      this.updateControls();
    }

    playCard(card) {
      const video = card.querySelector('video');
      const button = card.querySelector('[data-play]');
      if (button.hasAttribute('aria-busy')) return;
      this.videos.forEach((other) => { if (other !== video) other.pause(); });
      card.querySelector('.preme-reels__status').textContent = '';
      button.setAttribute('aria-busy', 'true');
      const poster = button.querySelector('img');
      if (poster) video.poster = poster.currentSrc || poster.src;
      let hasNewSource = false;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        if (!source.hasAttribute('src')) {
          source.src = source.dataset.src;
          hasNewSource = true;
        }
      });
      video.hidden = false;
      if (hasNewSource || video.error) video.load();
      if (video.ended) video.currentTime = 0;
      const playing = video.play();
      playing?.catch((error) => {
        button.removeAttribute('aria-busy');
        if (error.name === 'AbortError') {
          video.hidden = true;
          button.hidden = false;
        } else {
          this.showError(card);
        }
      });
    }

    showError(card) {
      const video = card.querySelector('video');
      const button = card.querySelector('[data-play]');
      video.pause();
      video.hidden = true;
      button.hidden = false;
      button.removeAttribute('aria-busy');
      card.querySelector('.preme-reels__status').textContent = this.dataset.error;
    }

    move(direction) {
      const card = this.cards[0];
      if (!card) return;
      const step = card.getBoundingClientRect().width + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
      this.track.scrollBy({ left: direction * step, behavior: this.motion.matches ? 'auto' : 'smooth' });
    }

    updateControls() {
      if (!this.cards.length) return;
      const scrollable = this.track.scrollWidth > this.track.clientWidth + 2;
      if (this.controls) this.controls.hidden = !scrollable;
      if (this.previous) this.previous.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      const left = this.track.getBoundingClientRect().left;
      let current = 0;
      let nearest = Infinity;
      this.cards.forEach((card, index) => {
        const distance = Math.abs(card.getBoundingClientRect().left - left);
        if (distance < nearest) { current = index; nearest = distance; }
      });
      if (this.position) this.position.textContent = this.dataset.position.replace('__current__', String(current + 1)).replace('__total__', String(this.cards.length));
    }

    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.videos?.forEach((video) => video.pause());
      this.events = null;
    }
  });
}
